/** Prefill Add knowledge wizard from browse screens (Phase C2) and product guide. */

const PREFILL_KEY = "dp_knowledge_prefill";
const OPEN_ADD_KEY = "dp_open_add_panel";
const KEEP_PREFILL_KEY = "dp_keep_knowledge_prefill";

/** In-memory copy so React Strict Mode remounts still see the same prefill. */
let cachedPrefill = null;
let openAddPending = false;
let keepPrefillPending = false;

function normalizePrefill(anchor) {
  if (!anchor) return null;
  const intent = String(anchor.intent || "").trim();
  const id = String(anchor.id || anchor.entityId || "").trim();
  if (!id && intent !== "create") return null;
  return {
    scopeType: anchor.scopeType || "entity",
    id,
    name: anchor.name || anchor.entityName || "",
    extractMode:
      anchor.extractMode || (anchor.extractEntities ? "force_on" : "auto"),
    intent: intent || undefined,
    suggestedName: anchor.suggestedName || "",
    suggestedType: anchor.suggestedType || "application",
    pasteText: anchor.pasteText || "",
    startStep: anchor.startStep || undefined,
    nextStepHint: anchor.nextStepHint || undefined,
    inputTab: anchor.inputTab || undefined,
  };
}

/**
 * @param {object} anchor
 * @param {string} [anchor.id] — existing entity/category id (optional when intent=create)
 * @param {string} [anchor.name]
 * @param {'entity'|'knowledge_surface'} [anchor.scopeType]
 * @param {string} [anchor.extractMode]
 * @param {'create'} [anchor.intent] — open Create entity with suggested name
 * @param {string} [anchor.suggestedName]
 * @param {string} [anchor.suggestedType]
 * @param {string} [anchor.pasteText] — prefill paste tab
 * @param {'scope'|'anchor'|'pick'|'source'} [anchor.startStep]
 * @param {'extract'|'example'} [anchor.nextStepHint] — one-line next-step cue on the source step
 */
export function stashKnowledgePrefill(anchor) {
  const payload = normalizePrefill(anchor);
  if (!payload) return;
  cachedPrefill = payload;
  openAddPending = true;
  try {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify(payload));
    sessionStorage.setItem(OPEN_ADD_KEY, "1");
  } catch {
    /* ignore storage errors */
  }
}

/** Read without clearing — safe across Strict Mode double-mount. */
export function peekKnowledgePrefill() {
  if (cachedPrefill) return { ...cachedPrefill };
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    const parsed = normalizePrefill(JSON.parse(raw));
    if (!parsed) return null;
    cachedPrefill = parsed;
    return { ...parsed };
  } catch {
    return null;
  }
}

export function clearKnowledgePrefill() {
  cachedPrefill = null;
  try {
    sessionStorage.removeItem(PREFILL_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Apply-once helper. Keeps the in-memory cache until {@link clearKnowledgePrefill}
 * so a Strict Mode remount can re-read the same payload.
 */
export function consumeKnowledgePrefill() {
  return peekKnowledgePrefill();
}

export function peekOpenAddPanel() {
  if (openAddPending) return true;
  try {
    return sessionStorage.getItem(OPEN_ADD_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearOpenAddPanel() {
  openAddPending = false;
  try {
    sessionStorage.removeItem(OPEN_ADD_KEY);
  } catch {
    /* ignore */
  }
}

/** True when Add panel should open; does not clear (use {@link clearOpenAddPanel}). */
export function consumeOpenAddPanel() {
  return peekOpenAddPanel();
}

export function navigateToAddKnowledge(onNavigate, homeRoute, anchor) {
  stashKnowledgePrefill(anchor);
  if (typeof onNavigate === "function" && homeRoute) {
    onNavigate(homeRoute);
  }
}

/**
 * Product guide / entity deep-link: Map open should not wipe a fresh prefill.
 * Call before navigating to Map Add knowledge; consumed once by openAddKnowledgeFlow.
 */
export function markKeepKnowledgePrefill() {
  keepPrefillPending = true;
  try {
    sessionStorage.setItem(KEEP_PREFILL_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** @returns {boolean} whether to preserve prefill (clears the keep flag). */
export function consumeKeepKnowledgePrefill() {
  let keep = keepPrefillPending;
  keepPrefillPending = false;
  try {
    if (sessionStorage.getItem(KEEP_PREFILL_KEY) === "1") keep = true;
    sessionStorage.removeItem(KEEP_PREFILL_KEY);
  } catch {
    /* ignore */
  }
  return keep;
}

/** Test-only: reset module caches between cases. */
export function __resetKnowledgePrefillForTests() {
  cachedPrefill = null;
  openAddPending = false;
  keepPrefillPending = false;
  try {
    sessionStorage.removeItem(PREFILL_KEY);
    sessionStorage.removeItem(OPEN_ADD_KEY);
    sessionStorage.removeItem(KEEP_PREFILL_KEY);
  } catch {
    /* ignore */
  }
}
